/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Box, Card, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { lighten } from '@mui/material/styles';

import { CIColors, ClusterStates, ServiceType } from 'shared';

import { ServiceIcon } from '../../../components/ServiceIcon';
import { BasicRoutes } from '../../../constants/basicRoutes';
import { HUChip } from '../../../elements/HUChip/HUChip';
import { determineStatusColor } from '../../../utils/determineStatusColor';
import { prepareRoutePath } from '../../../utils/prepareRoutePath';
import { FavoriteService } from '../../../utils/useFavoriteServices';

type Props = {
  serviceId: string;
  projectId: string;
  organizationId: string;
  k8sNamespace: string;
  name: string;
  type: ServiceType;
  status?: ClusterStates;
  instances: number;
  supportPlan?: {
    name: string;
    description: string;
    isDefault: boolean;
    price: number | string;
  } | null;
  favoriteServices: FavoriteService[];
  toggleFavorite: (event: React.MouseEvent, serviceId: string) => void;
};

export const ServiceCard = memo((props: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { organizationId, projectId, serviceId } = props;

  const onClick = useCallback(
    () =>
      navigate(
        prepareRoutePath(BasicRoutes.CLUSTER_PAGE, {
          ':organization_id': organizationId,
          ':project_id': projectId,
          ':cluster_id': serviceId,
        }),
      ),
    [navigate, organizationId, projectId, serviceId],
  );

  const serviceStatusLabel = useMemo(
    () => (props.status ? t(`entities:cluster.k8s_status.values.${props.status?.toLowerCase()}`) : '...'),
    [props.status, t],
  );

  const isFavorite = props.favoriteServices.some(fv => fv.id === props.serviceId);

  return (
    <Card
      elevation={2}
      sx={{
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        justifyContent: 'end',
        alignItems: 'center',
        borderRadius: '6px',
        '& #gradient': {
          borderRadius: '6px',
          transition: 'all .25s ease-in-out',
          background: `radial-gradient(circle at 115% 15%, ${CIColors.Secondary} 0%, #fff 45%)`,
          opacity: 0,
          position: 'absolute',
          width: '100%',
          height: '100%',
        },
        '& #icon': {
          transition: 'all .25s ease-in-out',
          position: 'absolute',
          opacity: 0.4,
          width: 'auto',
          height: '90px',
          marginRight: props.type === 'babelfish' ? '-14px' : '-35px',
          marginBottom: '35px',
        },
        '&:hover': {
          '#gradient': { opacity: 0.7 },
          '#icon': {
            opacity: 0.8,
            transform: 'scale(1.06)',
          },
          '#bookmark': {
            opacity: 1,
          },
        },
      }}
    >
      <Box id="gradient" />
      <ServiceIcon variant="minimal" id="icon" serviceType={props.type} />
      <Tooltip
        title={isFavorite ? t('cluster:details.header.favorite.remove')! : t('cluster:details.header.favorite.add')!}
      >
        <IconButton
          id="bookmark"
          data-cy={`project-page-favorite-cluster-button-${props.name}-${isFavorite}`}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '40px',
            height: '40px',
            padding: 0,
            backgroundColor: 'transparent',
            pointerEvents: 'auto',
            zIndex: 10,
            opacity: isFavorite ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
            '&:hover': { opacity: 1 },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '40px',
              height: '40px',
              background: isFavorite
                ? `linear-gradient(to bottom right, ${CIColors.Tertiary}, ${lighten(CIColors.Tertiary, 0.2)})`
                : `linear-gradient(to bottom right, ${CIColors.GraphicElements}, ${lighten(
                    CIColors.GraphicElements,
                    0.2,
                  )})`,
              clipPath: 'polygon(0 0, 100% 0, 0 100%)',
              boxShadow: '3px 3px 8px rgba(0,0,0,0.2)',
              zIndex: 1,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '5px',
              left: '5px',
              width: '30px',
              height: '30px',
              background: isFavorite
                ? `linear-gradient(to bottom right, ${lighten(CIColors.Tertiary, 0.3)}, ${lighten(
                    CIColors.Tertiary,
                    0.1,
                  )})`
                : `linear-gradient(to bottom right, ${lighten(CIColors.GraphicElements, 0.3)}, ${lighten(
                    CIColors.GraphicElements,
                    0.1,
                  )})`,
              clipPath: 'polygon(0 0, 100% 0, 0 100%)',
              boxShadow: 'inset -1px -1px 5px rgba(0,0,0,0.15)',
              zIndex: 2,
            },
          }}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            props.toggleFavorite(e, props.serviceId);
          }}
        >
          <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
            <polygon points="0,0 40,0 0,40" fill="transparent" />
          </svg>
        </IconButton>
      </Tooltip>
      <Box
        position="relative"
        height="100%"
        px={3}
        pt={3}
        pb={2}
        width="280px"
        sx={{
          '&:hover': { cursor: 'pointer' },
        }}
        data-cy={`cluster-list-${props.name}`}
        data-testid={`cluster-${props.serviceId}`}
        onClick={onClick}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
      >
        <Box>
          <h3 style={{ margin: '0' }}>{props.name}</h3>
          <Tooltip arrow enterDelay={600} enterNextDelay={600} title={t('cluster:overview.table.tooltips.namespace')}>
            <Box component="span" fontSize="13px">
              {props.k8sNamespace}
            </Box>
          </Tooltip>
        </Box>

        <Stack mt={1}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" gap={0.1} alignItems="center">
              <Tooltip
                arrow
                enterDelay={600}
                enterNextDelay={600}
                title={t('cluster:overview.table.tooltips.supportPlan')}
              >
                <Typography variant="subtitle2" sx={{ fontSize: '13px', textTransform: 'capitalize' }}>
                  {props.supportPlan?.name}
                </Typography>
              </Tooltip>

              <Typography>-</Typography>
              <Tooltip
                arrow
                enterDelay={600}
                enterNextDelay={600}
                title={t('cluster:overview.table.tooltips.instances')}
              >
                <Typography variant="subtitle2" sx={{ fontSize: '13px' }}>
                  {t('cluster:instance', { count: props.instances })}
                </Typography>
              </Tooltip>
            </Stack>
            <Tooltip arrow enterDelay={600} enterNextDelay={600} title={t('cluster:overview.table.tooltips.status')}>
              <Box>
                <HUChip
                  sx={{ backgroundColor: '#FFF !important', fontFamily: 'monospace' }}
                  size="small"
                  variant="outlined"
                  label={serviceStatusLabel}
                  color={determineStatusColor(props.status)}
                />
              </Box>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
    </Card>
  );
});
